import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { CalendarIcon, UserIcon, TagIcon, ShareIcon, ArrowLeftIcon, ArrowRightIcon, FacebookIcon, TwitterIcon, LinkedinIcon } from 'lucide-react';
import { useSiteContent } from '../contexts/SiteContentContext';
const NewsDetail = () => {
  const {
    getPublishedArticles
  } = useSiteContent();
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [article, setArticle] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [relatedArticles, setRelatedArticles] = useState<any[]>([]);
  const allArticles = getPublishedArticles();
  useEffect(() => {
    setLoading(true);
    const foundArticle = allArticles.find(article => article.slug === slug);
    if (foundArticle) {
      setArticle(foundArticle);
      const related = allArticles.filter(a => a.category === foundArticle.category && a.id !== foundArticle.id).slice(0, 2);
      setRelatedArticles(related);
    }
    setLoading(false);
  }, [slug, allArticles]);
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  const handleShare = (platform: string) => {
    const url = window.location.href;
    const title = article?.title || 'News from Isonyin Grammar School';
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${url}&text=${title}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${url}&title=${title}`, '_blank');
        break;
      default:
        navigator.clipboard.writeText(url).then(() => alert('Link copied to clipboard!')).catch(err => console.error('Failed to copy: ', err));
    }
  };
  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center" data-id="element-5593">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-900" data-id="element-5594"></div>
      </div>;
  }
  if (!article) {
    return <div className="min-h-screen bg-white" data-id="element-5595">
        <PageHeader title="Article Not Found" description="The article you are looking for does not exist or has been moved." backgroundImage="https://uploadthingy.s3.us-west-1.amazonaws.com/hgRGwnKLZHVDTRdgag3Y5F/WhatsApp_Image_2025-10-24_at_10.01.11.jpg" breadcrumbs={[{
        label: 'Home',
        path: '/'
      }, {
        label: 'News',
        path: '/news'
      }, {
        label: 'Not Found',
        path: `/news/${slug}`
      }]} data-id="element-5596" />
        <div className="container mx-auto px-4 py-12 text-center" data-id="element-5597">
          <h2 className="text-3xl font-bold text-blue-900 mb-6" data-id="element-5598">
            Article Not Found
          </h2>
          <p className="text-gray-700 mb-8" data-id="element-5599">
            The article you are looking for does not exist or has been moved.
          </p>
          <Button href="/news" variant="primary" icon={<ArrowLeftIcon data-id="element-5601" />} iconPosition="left" data-id="element-5600">
            Back to News
          </Button>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-white" data-id="element-5602">
      <PageHeader title={article.title} description={article.excerpt} backgroundImage={article.image} breadcrumbs={[{
      label: 'Home',
      path: '/'
    }, {
      label: 'News',
      path: '/news'
    }, {
      label: article.title,
      path: `/news/${article.slug}`
    }]} data-id="element-5603" />
      <div className="container mx-auto px-4 py-12" data-id="element-5604">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-5605">
          <div className="lg:col-span-2" data-id="element-5606">
            <article className="bg-white rounded-lg shadow-md p-6 lg:p-8" data-id="element-5607">
              <div className="flex flex-wrap items-center text-gray-500 text-sm mb-6 gap-x-4 gap-y-2" data-id="element-5608">
                <div className="flex items-center" data-id="element-5609">
                  <CalendarIcon className="h-4 w-4 mr-1" data-id="element-5610" />
                  <span data-id="element-5611">{formatDate(article.date)}</span>
                </div>
                <div className="flex items-center" data-id="element-5612">
                  <UserIcon className="h-4 w-4 mr-1" data-id="element-5613" />
                  <span data-id="element-5614">{article.author}</span>
                </div>
                <div className="flex items-center" data-id="element-5615">
                  <TagIcon className="h-4 w-4 mr-1" data-id="element-5616" />
                  <span className="capitalize" data-id="element-5617">{article.category}</span>
                </div>
              </div>
              <div className="prose prose-blue max-w-none mb-8" dangerouslySetInnerHTML={{
              __html: article.content
            }} data-id="element-5618" />
              <div className="border-t border-gray-200 pt-6 mt-8" data-id="element-5619">
                <div className="flex flex-wrap justify-between items-center" data-id="element-5620">
                  <div className="mb-4 md:mb-0" data-id="element-5621">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-5622">
                      Tags:
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-5623">
                      {article.tags.map((tag: string, index: number) => <Link key={index} to={`/news?tag=${tag}`} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-full text-sm flex items-center transition-colors" data-id="element-5624">
                          <TagIcon className="h-3 w-3 mr-1" data-id="element-5625" />
                          {tag}
                        </Link>)}
                    </div>
                  </div>
                  <div data-id="element-5626">
                    <p className="text-sm font-medium text-gray-500 mb-2" data-id="element-5627">
                      Share:
                    </p>
                    <div className="flex space-x-2" data-id="element-5628">
                      <button onClick={() => handleShare('facebook')} className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" aria-label="Share on Facebook" data-id="element-5629">
                        <FacebookIcon className="h-4 w-4" data-id="element-5630" />
                      </button>
                      <button onClick={() => handleShare('twitter')} className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" aria-label="Share on Twitter" data-id="element-5631">
                        <TwitterIcon className="h-4 w-4" data-id="element-5632" />
                      </button>
                      <button onClick={() => handleShare('linkedin')} className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" aria-label="Share on LinkedIn" data-id="element-5633">
                        <LinkedinIcon className="h-4 w-4" data-id="element-5634" />
                      </button>
                      <button onClick={() => handleShare('copy')} className="p-2 bg-gray-200 text-gray-700 rounded-full hover:bg-gray-300 transition-colors" aria-label="Copy link" data-id="element-5635">
                        <ShareIcon className="h-4 w-4" data-id="element-5636" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
            <div className="mt-8 flex justify-between" data-id="element-5637">
              <Button href="/news" variant="outline" icon={<ArrowLeftIcon data-id="element-5639" />} iconPosition="left" data-id="element-5638">
                Back to News
              </Button>
              <Button href={`/news/${relatedArticles.length > 0 ? relatedArticles[0].slug : ''}`} variant="outline" icon={<ArrowRightIcon data-id="element-5641" />} iconPosition="right" disabled={relatedArticles.length === 0} data-id="element-5640">
                Next Article
              </Button>
            </div>
          </div>
          <div data-id="element-5642">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-5643">
              <h3 className="text-xl font-bold text-blue-900 mb-4 border-b border-gray-200 pb-2" data-id="element-5644">
                Related Articles
              </h3>
              {relatedArticles.length > 0 ? <div className="space-y-4" data-id="element-5645">
                  {relatedArticles.map(related => <div key={related.id} className="flex items-start" data-id="element-5646">
                      <img src={related.image} alt={related.title} className="w-20 h-16 object-cover rounded mr-3 flex-shrink-0" data-id="element-5647" />
                      <div data-id="element-5648">
                        <h4 className="font-bold text-blue-900 hover:text-blue-700 transition-colors line-clamp-2" data-id="element-5649">
                          <Link to={`/news/${related.slug}`} data-id="element-5650">
                            {related.title}
                          </Link>
                        </h4>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-5651">
                          {formatDate(related.date)}
                        </p>
                      </div>
                    </div>)}
                </div> : <p className="text-gray-700" data-id="element-5652">No related articles found.</p>}
              <div className="mt-6" data-id="element-5653">
                <Button href="/news" variant="outline" size="sm" className="w-full" data-id="element-5654">
                  View All News
                </Button>
              </div>
            </div>
            <div className="bg-blue-50 rounded-lg border border-blue-100 p-6" data-id="element-5655">
              <h3 className="text-xl font-bold text-blue-900 mb-4" data-id="element-5656">
                Subscribe to Newsletter
              </h3>
              <p className="text-gray-700 mb-4" data-id="element-5657">
                Stay updated with the latest news and events from Isonyin
                Grammar School.
              </p>
              <form className="space-y-3" onSubmit={e => {
              e.preventDefault();
              alert('Subscription successful!');
            }} data-id="element-5658">
                <div data-id="element-5659">
                  <input type="email" placeholder="Your Email Address" className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-5660" />
                </div>
                <Button type="submit" variant="secondary" className="w-full" data-id="element-5661">
                  Subscribe
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default NewsDetail;